import { useState, ChangeEvent, useEffect } from 'react';
import Button from '../../components/reusable/Button';
import Modal from '../../components/reusable/Modal';
import {
  DataFixScriptSection,
  IncidentDetailsSection,
  DynamicParametersSection,
  ParameterField,
  PreviewSection,
} from '../../components/sections';
import '../../styles/ScriptRunUI.css';
import { ParameterRow } from '../../components/sections/DynamicParametersSection';
import { PageScriptActionsService } from '../../services/PageScriptActionsService';
import { SCRIPT_CONFIGS, SCRIPT_OPTIONS } from '../../config/scriptConfigs';
import {
  ADD_UNDERWRITER_ENVIRONMENT_IMPACT,
  DEFAULT_ENVIRONMENT_IMPACT,
  DEFAULT_PARAMETER_ROW_ID,
  EXECUTION_DELAY_MS,
  INCIDENT_ID_INVALID_CHARS,
  MESSAGES,
  PRODUCTION_ENVIRONMENT_IMPACT,
  REQUESTER_EMAIL_AUTOPOPULATE_DELAY_MS,
  SCRIPT,
  SUBCOMPANY_CODE_VALID,
} from '../../config/scriptRunConstants';
import Breadcrumb from "@/components/bread-crumb/BreadCrumb";
import { useParams } from 'react-router-dom';

// Case-insensitive key map: matches PascalCase API keys to camelCase param names
const mapPostToParamValues = (
  source: Record<string, unknown>,
  params: ParameterField[]
): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  const normalized = Object.fromEntries(
    Object.entries(source).map(([k, v]) => [k.toLowerCase(), v])
  );
  params.forEach(param => {
    const val = normalized[param.name.toLowerCase()];
    if (val !== undefined && val !== null) {
      result[param.name] = typeof val === 'number' ? val : String(val);
    }
  });
  return result;
};
const ScriptRunUI = () => {
  const createInitialRow = (): ParameterRow => ({ id: DEFAULT_PARAMETER_ROW_ID, values: {} });
  // <-- Read the param by its exact name
  const { incidentParam } = useParams<{ incidentParam?: string }>();

  // State management
  const [selectedScript, setSelectedScript] = useState('');
  const [parameterValues, setParameterValues] = useState<Record<
    string, string | number>>({});
  const [parameterRows, setParameterRows] = useState<ParameterRow[]>([createInitialRow()]); // For multi-row scripts
  const [incidentId, setIncidentId] = useState('');
  const [isExecuteDisabled, setExecuteDisable] = useState(false);
  const [requesterEmail, setRequesterEmail] = useState('');
  const [environmentImpact, setEnvironmentImpact] = useState(DEFAULT_ENVIRONMENT_IMPACT);
  const [publishToProd, setPublishToProd] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewData, setPreviewData] = useState<{
    before: ParameterRow[];
    after: ParameterRow[];
  }>({
    before: [],
    after: [],
  });
  const [showPreview, setShowPreview] = useState(false);
  const [isExecuted, setIsExecuted] = useState(false);
  const [previewFields, setPreviewFields] = useState<ParameterField[]>([]);
  const [incidentIdValid, setIncidentIdValid] = useState(false);
  const getDefaultEnvironmentImpact = (scriptName: string, isProd: boolean = publishToProd) => {
    if (isProd) return PRODUCTION_ENVIRONMENT_IMPACT;
    return scriptName === SCRIPT.insert_underwriter
      ? ADD_UNDERWRITER_ENVIRONMENT_IMPACT
      : DEFAULT_ENVIRONMENT_IMPACT;
  };
  // Read params from route:
  // /DataScriptApproval/:incidentNumber
  // /DataScriptApproval/:incidentNumber/:approvalTarget
  const { incidentNumber: rawIncidentNumber, approvalTarget: rawApprovalTarget } = useParams<{
    incidentNumber: string;
    approvalTarget?: string;
  }>();
  const incidentNumber = rawIncidentNumber?.toUpperCase();
  const approvalTarget = rawApprovalTarget?.trim().toLowerCase() || null;
  // Auto-bind all form data from URL incident ID
  useEffect(() => {

    const fromUrl = (incidentParam ?? "").trim();
    if (!fromUrl) return;
    const isPaymentPortalExists = approvalTarget?.includes('paymentportal') ?? false;
    setIncidentId(fromUrl);
    (async () => {
      try {
        const data = await PageScriptActionsService.fetchIncidentFormData(fromUrl, isPaymentPortalExists);
        if (data.spName) setSelectedScript(data.spName);
        if (data.requestorEmail) {
          setRequesterEmail(data.requestorEmail);
          setIncidentIdValid(true);
        }
        if (data.isApproved !== undefined) setIsApproved(data.isApproved ?? null);
        // Auto-check publishToProd if approved OR already published
        if (data.isApproved === true || data.isPublishedToProd === true) {
          setPublishToProd(true);
        } else if (data.isPublishedToProd !== undefined) {
          setPublishToProd(data.isPublishedToProd);
        }
        // Parse formData JSON string from first audit entry
        let audit = data.smallDataFixAudits?.[0];
        if
          (audit?.environment) {
          if (data.isApproved === true)
            setEnvironmentImpact(PRODUCTION_ENVIRONMENT_IMPACT);
          else
            setEnvironmentImpact(audit.environment);
          //
          if (audit?.environment == PRODUCTION_ENVIRONMENT_IMPACT && data.isApproved === true &&
            audit?.executedBy) {
            setExecuteDisable(true);
          }
          else {
            setExecuteDisable(false);
          }
        }

        let parsedFormData: { pre?: unknown; post?: unknown } = {};
        if (audit?.formData) {
          try { parsedFormData = JSON.parse(audit.formData); } catch { parsedFormData = {}; }
        }
        const scriptConfig = data.spName ? SCRIPT_CONFIGS[data.spName] : null;
        // Bind post data into parameter fields using case-insensitive key matching
        const pre = parsedFormData.pre;
        const post = parsedFormData.post;
        if (post && scriptConfig) {
          if (scriptConfig.supportsMultiRow) {
            // Zip pre + post rows so each row gets fields from both (e.g. claimNumberFrom + claimNumberTo)
            const preArr = Array.isArray(pre) ? pre as Record<string, unknown>[] : [];
            const postArr = Array.isArray(post) ? post as Record<string, unknown>[] : [post as Record<string, unknown>];
            const maxLen = Math.max(preArr.length, postArr.length);
            setParameterRows(
              Array.from({ length: maxLen }, (_, i) => {
                const merged = { ...(preArr[i] ?? {}), ...(postArr[i] ?? {}) };
                return {
                  id: String(i + 1),
                  values: mapPostToParamValues(merged, scriptConfig.parameters),
                };
              })
            );
          } else {
            const postObj = Array.isArray(post) ? post[0] : post;
            if (postObj && typeof postObj === 'object') {
              setParameterValues(
                mapPostToParamValues(postObj as Record<string, unknown>, scriptConfig.parameters)
              );
            }
          }
        }
        // // Build preview rows from pre / post
        // const toRows = (source: unknown): ParameterRow[] => {
        //   const items = Array.isArray(source) ? source : [source];
        //   return items
        //     .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
        //     .map((r, i) => ({
        //       id: String(i + 1),
        //       values: Object.fromEntries(
        //         Object.entries(r).map(([k, v]) => [
        //           k,
        //           typeof v === 'number' || typeof v === 'string' ? v : v == null ? '' : String(v),
        //         ])
        //       ),
        //     }));
        // };
        // const before = toRows(data.pre);
        // const after = toRows(data.post);
        // if (before.length > 0 || after.length > 0) {
        //   const allKeys = Array.from(
        //     new Set([...before, ...after].flatMap(r => Object.keys(r.values)))
        //   );
        //   setPreviewData({ before: before.length > 0 ? before : after, after });
        //   setPreviewFields(allKeys.map(k => ({ name: k, label: k, type: 'text' as const })));
        //   setShowPreview(true);
        // }
      } catch {
        // silently ignore on auto-load errors
      }
    })();
  }, []);
  // Get current script configuration
  const currentScript = selectedScript ? SCRIPT_CONFIGS[selectedScript] : null;
  const currentScriptOption = SCRIPT_OPTIONS.find((opt) => opt.value === selectedScript);
  // Derived UI flags based on approval and publish state
  const isInitiateEmail = publishToProd && !isApproved;
  const isPreviewDisabled = publishToProd;
  // Handlers
  const handleScriptChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const nextScript = e.target.value;
    setSelectedScript(nextScript);
    setEnvironmentImpact(getDefaultEnvironmentImpact(nextScript));
    setParameterValues({}); // Reset parameters when script changes
    setParameterRows([createInitialRow()]); // Reset multi-row parameters
    setShowPreview(false);
    setIsExecuted(false);
    setPreviewFields([]);
    setIncidentId('');
    setIncidentIdValid(false);
    setRequesterEmail('');
  };
  const hasValidParameters = (): boolean => {
    if (!currentScript) return false;
    const isRequiredValueFilled = (value: string | number | undefined): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      return true;
    };
    if (currentScript.supportsMultiRow) {
      return !parameterRows.some((row) =>
        currentScript.parameters.some(
          (param) => param.required && !isRequiredValueFilled(row.values[param.name])
        )
      );
    }
    return !currentScript.parameters.some(
      (param) => param.required && !isRequiredValueFilled(parameterValues[param.name])
    );
  };
  // Check if entire form is valid (for disabling Execute button)
  const isFormValid = (): boolean => {
    if (!currentScript) return false;
    if (!selectedScript) return false;
    if (!incidentId.trim()) return false;
    if (!incidentIdValid) return false;
    if (!requesterEmail.trim()) return false;
    if (!hasValidParameters()) return false;
    return true;
  };
  const handleParameterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setParameterValues(prev => ({ ...prev, [name]: value }));
  };
  // Multi-row handlers
  const handleAddRow = () => {
    const newId = (parameterRows.length + 1).toString();
    setParameterRows([...parameterRows, { id: newId, values: {} }]);
  };
  const handleDeleteRow = (rowId: string) => {
    setParameterRows(parameterRows.filter(row => row.id !== rowId));
  };
  const handleRowChange = (rowId: string, name: string, value: string | number) => {
    setParameterRows(parameterRows.map(row =>
      row.id === rowId
        ? { ...row, values: { ...row.values, [name]: value } }
        : row
    ));
  };
  const handleIncidentBlur = async (e: ChangeEvent<HTMLInputElement>) => {
    const incidentValue = e.target.value.trim();
    if (!incidentValue) {
      setRequesterEmail('');
      setIncidentIdValid(false);
      return;
    }
    // Validate incident ID for special characters
    if (INCIDENT_ID_INVALID_CHARS.test(incidentValue)) {
      setRequesterEmail('');
      setIncidentIdValid(false);
      setErrorMessage(MESSAGES.incorrectIncidentId);
      setShowErrorModal(true);
      return;
    }
    try {
      const email = await PageScriptActionsService.fetchRequesterEmailByIncidentId(incidentValue);
      const requestorEmail = (email as any)?.requestorEmail ?? '';
      setRequesterEmail(requestorEmail);
      setIncidentIdValid(true);
    } catch (error) {
      setRequesterEmail('');
      setIncidentIdValid(false);
      setErrorMessage(MESSAGES.incorrectIncidentId);
      setShowErrorModal(true);
      return;
    }
  };
  const handleIncidentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIncidentId(e.target.value);
    // Reset validation when user types a new value
    setIncidentIdValid(false);
    setRequesterEmail('');
  };
  const handlePublishChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      setShowPublishModal(true);
    } else {
      setPublishToProd(false);
      setEnvironmentImpact(getDefaultEnvironmentImpact(selectedScript, false));
    }
  };
  const confirmPublishToProd = () => {
    setPublishToProd(true);
    setEnvironmentImpact(PRODUCTION_ENVIRONMENT_IMPACT);
    setShowPublishModal(false);
  };
  const cancelPublishToProd = () => {
    setPublishToProd(false);
    setEnvironmentImpact(getDefaultEnvironmentImpact(selectedScript, false));
    setShowPublishModal(false);
  };
  const handleReset = () => {
    setShowResetModal(true);
  };
  const confirmReset = () => {
    setSelectedScript('');
    setParameterValues({});
    setParameterRows([createInitialRow()]); // Reset multi-row parameters
    setIncidentId('');
    setRequesterEmail('');
    setEnvironmentImpact(DEFAULT_ENVIRONMENT_IMPACT);
    setPublishToProd(false);
    setShowPreview(false);
    setIsExecuted(false);
    setPreviewData({ before: [], after: [] });
    setPreviewFields([]);
    setShowResetModal(false);
    setIncidentIdValid(false);
  };
  const getEndPointErrorMessage = (error: any) => {
    const data = error?.response?.data;
    if (data?.errors && typeof data.errors === 'object') {
      const parts: string[] = [];
      for (const [field, msgs] of Object.entries<any>(data.errors)) {
        if (Array.isArray(msgs) && msgs.length) {
          parts.push(`${field}: ${msgs.join(', ')}`);
        }
      }
      if (parts.length) return parts.join(' | ');
    }
    if (data) {
      if (typeof data === 'string') return data;
      if (typeof data.message === 'string' && data.message.trim()) return data.message;
    }
    if (typeof Response !== 'undefined' && error instanceof Response) {
      return error.statusText || `HTTP ${error.status}`;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  };
  const InvoicePreview = async () => {
    return await PageScriptActionsService.invoicePreview({
      policyId: Number(parameterValues.policyId),
      acctInvoiceId: Number(parameterValues.acctInvoiceId),
      incidentId: incidentId,
      isProd: publishToProd
    });
  }
  const ClaimNumberUpdatePreview = async () => {
    const claimNums = parameterRows
      .map(r => (r.values as any)?.claimNumberFrom)
      .filter(Boolean)
      .map(v => String(v).trim())
      .filter(v => v.length > 0)
      .join(',');
    return await PageScriptActionsService.claimNumberUpdatePreview({
      claimNums,
      incidentId: incidentId,
      isProd: publishToProd
    });
  }
  const RestoreClaimCoverageFromHistoryPreview = async () => {
    return await PageScriptActionsService.restoreClaimCoverageFromHistoryPreview({
      incidentId: incidentId,
      isProd: publishToProd
    });
  }
  const UWLimitPreview = async () => {
    return await PageScriptActionsService.uwLimitPreview({
      uwLimitId: parameterRows.map(r => String(r.values.uwLimitId ?? '').trim())
        .filter(Boolean)
        .join(','),
      incidentId: incidentId,
      isProd: publishToProd
    });
  }
  const ClaimCoverageLimitPreview = async () => {
    return await PageScriptActionsService.claimCoverageLimitPreview({
      claimCovLimitId: parameterRows.map(r => String(r.values.claimCovLimitId ?? '').trim())
        .filter(Boolean)
        .join(','),
      incidentId: incidentId,
      isProd: publishToProd
    });
  }
  const ClaimCoveragePreview = async () => {
    return await PageScriptActionsService.claimCoveragePreview({
      selectedScript,
      parameters: currentScript?.supportsMultiRow ? parameterRows : parameterValues,
      incidentId,
      requesterEmail,
      environmentImpact,
      publishToProd,
    });
  }
  const InvoiceExecute = async () => {
    return await PageScriptActionsService.invoiceExecute({
      policyId: Number(parameterValues.policyId),
      acctInvoiceId: Number(parameterValues.acctInvoiceId),
      newPaidTotal: Number(parameterValues.paidTotal),
      newInvoiceStatus: String(parameterValues.invoiceStatus || 'PAID'),
      newAcctMasterId: Number(parameterValues.acctMasterId),
      acctInvoiceActId: Number(parameterValues.acctInvoiceActivityId),
      acctPayableId: Number(parameterValues.acctPayableId),
      acctPayableActivityId: Number(parameterValues.acctPayableActivityId),
      //common params
      IsPublished: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      requestorEmail: requesterEmail,
      snowIncidentNumber: incidentId,
      selectedScriptName: selectedScript,
      ExecutedBy: sessionStorage?.getItem("userId")
    });
  }
  const ClaimNumberUpdateExecute = async () => {
    const claims = parameterRows
      .map(row => ({
        sourceClaimNum: String((row.values as any)?.claimNumberFrom ?? '').trim(),
        targetClaimNum: String((row.values as any)?.claimNumberTo ?? '').trim(),
      }))
      .filter(c => c.sourceClaimNum && c.targetClaimNum);
    return await PageScriptActionsService.claimNumberUpdateExecute({
      claims,
      //common params
      IsPublished: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      requestorEmail: requesterEmail,
      snowIncidentNumber: incidentId,
      selectedScriptName: selectedScript,
      ExecutedBy: sessionStorage?.getItem("userId"),
    });
  }
  const RestoreClaimCoverageFromHistoryExecute = async () => {
    return await PageScriptActionsService.restoreClaimCoverageFromHistoryExecute({
      item: parameterRows.map((row) => ({
        claimcovId: Number(row.values.claimcovId ?? ''),
        claimid: Number(row.values.claimid ?? ''),
      })),
      environment: environmentImpact,
      executedBy: sessionStorage?.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      requestorEmail: requesterEmail,
      snowIncidentNumber: incidentId,
      spName: selectedScript
    });
  }
  const UWLimitExecute = async () => {
    return await PageScriptActionsService.uwLimitExecute({
      item: parameterRows.map((row) => ({
        uwLimitId: Number(row.values.uwLimitId ?? ''),
        limitValue: String(row.values.limitValue ?? ''),
      })),
      environment: environmentImpact,
      executedBy: sessionStorage?.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      requestorEmail: requesterEmail,
      snowIncidentNumber: incidentId,
      spName: selectedScript
    });
  }
  const ClaimCoverageLimitExecute = async () => {
    return await PageScriptActionsService.claimCoverageLimitExecute({
      item: parameterRows.map((row) => ({
        claimCovLimitId: Number(row.values.claimCovLimitId ?? ''),
        limitValue: String(row.values.limitValue ?? ''),
      })),
      environment: environmentImpact,
      executedBy: sessionStorage?.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      requestorEmail: requesterEmail,
      snowIncidentNumber: incidentId,
      spName: selectedScript
    });
  }
  const ClaimCoverageExecute = async () => {
    return await PageScriptActionsService.claimCoverageExecute({
      selectedScript,
      parameters: currentScript?.supportsMultiRow ? parameterRows : parameterValues,
      incidentId,
      requesterEmail,
      environmentImpact,
      publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
    });
  }
  const ParentAgencyPreview = async () => {
    const contactIds = parameterRows
      .map(r => String((r.values as any)?.contactId || "").trim())
      .filter(Boolean)
      .join(",");
    return await PageScriptActionsService.parentAgencyPreview({
      snowIncidentNumber: incidentId,
      requestorEmail: requesterEmail ?? "",
      spName: selectedScript,
      environment: environmentImpact,
      executedBy: sessionStorage?.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      contactIds: contactIds
    });
  }
  const ParentAgencyExecute = async () => {
    const contactIds = parameterRows.map(row => Number(row.values?.contactId));
    return await PageScriptActionsService.parentAgencyExecute({
      snowIncidentNumber: incidentId,
      requestorEmail: requesterEmail ?? "",
      spName: selectedScript,
      environment: environmentImpact,
      executedBy: sessionStorage.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      contactIds: contactIds
    });
  }
  const InsertUnderwriterExecute = async () => {
    const underwriters = parameterRows.map(row => ({
      userMail: String((row.values as any)?.userEmail || "").trim(),
      subCompanyCode: String((row.values as any)?.subCompanyCode || "").trim(),
    }));
    return await PageScriptActionsService.insertUnderwriterExecute({
      snowIncidentNumber: incidentId,
      requestorEmail: requesterEmail ?? "",
      selectedScriptName: selectedScript,
      environment: environmentImpact,
      executedBy: sessionStorage?.getItem("userId"),
      isPublishedToProd: publishToProd,
      isApproved: isApproved ?? false,
      execute: !isInitiateEmail,
      underwriters: underwriters
    });
  }
  const InsertUnderwriterPreview = async () => {

    return await PageScriptActionsService.insertUnderwriterPreview({
      incidentId: incidentId,
      isProd: publishToProd
    });
  }

  const handlePreview = async () => {
    if (!currentScript) return;
    if (!hasValidParameters()) {
      setErrorMessage(MESSAGES.requiredFields);
      setShowErrorModal(true);
      return;
    }
    try {
      let response: unknown = undefined;
      if (selectedScript === SCRIPT.update_paid_invoice) {
        response = await InvoicePreview();
      }
      else if (selectedScript === SCRIPT.update_parent_agency_kraft_lake) {
        response = await ParentAgencyPreview();
      }
      else if (selectedScript === SCRIPT.update_claims_number) {
        let data: any = await ClaimNumberUpdatePreview();
        response = JSON.parse(data);
      }
      else if (selectedScript === SCRIPT.insert_underwriter) {
        response = await InsertUnderwriterPreview();
      }
      else if (selectedScript === SCRIPT.update_coverage_preview) {
      }
      else if (selectedScript === SCRIPT.update_deleted_claims) {
        response = await RestoreClaimCoverageFromHistoryPreview();
      }
      else if (selectedScript === SCRIPT.update_uw_limits) {
        response = await UWLimitPreview();
      }
      else if (selectedScript === SCRIPT.update_claims_coverage_limits) {
        response = await ClaimCoverageLimitPreview();
      }
      else {
        response = await ClaimCoveragePreview();
      }
      if (response && typeof response === 'object') {
        const previewResponse = response as {
          pre?: unknown;
          post?: unknown;
          before?: unknown;
          after?: unknown;
        };
        const mapRows = (source: unknown): ParameterRow[] => {
          const rows = Array.isArray(source) ? source : [source];
          return rows
            .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
            .map((row, index) => ({
              id: `${index + 1}`,
              values: Object.fromEntries(
                Object.entries(row).map(([key, value]) => [
                  key,
                  typeof value === 'number' || typeof value === 'string'
                    ? value
                    : value == null
                      ? ''
                      : String(value),
                ])
              ),
            }));
        };
        let before = mapRows(previewResponse.pre ?? previewResponse.before);
        let after = mapRows(previewResponse.post ?? previewResponse.after);
        if (selectedScript === SCRIPT.update_deleted_claims) {
          if (before.length === 0 && after.length > 0) {
            before = after.map((row, index) => ({
              id: `${index + 1}`,
              values: Object.fromEntries(
                Object.keys(row.values).map(key => [key, ''])
              )
            }));
          }
        }
        if (before.length > 0 || after.length > 0) {
          const allRows = [...before, ...after];
          const keys = Array.from(new Set(allRows.flatMap((row) => Object.keys(row.values))));
          setPreviewData({
            before: selectedScript === SCRIPT.insert_underwriter ? before : before.length > 0 ? before : after,
            after,
          });
          setPreviewFields(
            keys.length > 0
              ? keys.map((key) => ({ name: key, label: key, type: 'text' }))
              : currentScript.parameters
          );
          setShowPreview(true);
          return;
        }
      }
    } catch (error) {
      const msg = getEndPointErrorMessage(error);
      setErrorMessage(msg);
      setShowErrorModal(true);
      return;
    }
    setPreviewData({ before: [], after: [] });
    setPreviewFields(currentScript.parameters);
    setShowPreview(true);
  };
  const isExecuteActionDisabled =
    isExecuteDisabled || !isFormValid();
  /**
  * Email validation utility
  * Validates email format according to standard email regex pattern
  */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };
  /**
  * Validates all emails in a list
  */
  const areAllEmailsValid = (emails: (string | number | undefined)[]): boolean => {
    return emails.every((email) => {
      if (!email) return false;
      return isValidEmail(String(email));
    });
  };
  /**
  * Validates all subcompany codes contain only letters and numbers (no special characters)
  */
  const areAllSubcompanyCodesValid = (codes: (string | number | undefined)[]): boolean => {
    return codes.every((code) => {
      if (!code) return false;
      return SUBCOMPANY_CODE_VALID.test(String(code).trim());
    });
  };
  const handleExecute = () => {
    if (!currentScript) return;
    if (!isFormValid()) {
      setErrorMessage(MESSAGES.requiredFields);
      setShowErrorModal(true);
      return;
    }
    // Validations for insert-underwriter script
    if (selectedScript === SCRIPT.insert_underwriter) {
      // Subcompany code validation - only letters and numbers allowed, no special characters
      const subCompanyCodes = parameterRows.map((row) =>
        String((row.values as any)?.subCompanyCode || '').trim()
      );
      if (!areAllSubcompanyCodesValid(subCompanyCodes)) {
        setErrorMessage(MESSAGES.invalidSubcompany);
        setShowErrorModal(true);
        return;
      }
      // Email validation
      const userEmails = parameterRows.map((row) =>
        String((row.values as any)?.userEmail || '').trim()
      );
      if (!areAllEmailsValid(userEmails)) {
        setErrorMessage('Please enter valid user email.');
        setShowErrorModal(true);
        return;
      }
    }
    setShowExecuteModal(true);
  };
  const confirmExecute = async () => {
    setShowExecuteModal(false);
    try {
      if (selectedScript === SCRIPT.update_paid_invoice) {
        await InvoiceExecute();
      }
      else if (selectedScript === SCRIPT.update_claims_number) {
        try {
          await ClaimNumberUpdateExecute();
          setIsExecuted(true);
          setShowSuccessModal(true);
          setShowPreview(false);
        } catch (err) {
          console.error('Claim number execute call failed:', err);
          setErrorMessage(MESSAGES.executionFailed);
          setShowErrorModal(true);
          return;
        }
      }
      else if (selectedScript === SCRIPT.update_deleted_claims) {
        await RestoreClaimCoverageFromHistoryExecute();
      }
      else if (selectedScript === SCRIPT.update_uw_limits) {
        await UWLimitExecute();
      }
      else if (selectedScript === SCRIPT.update_claims_coverage_limits) {
        await ClaimCoverageLimitExecute();
      }
      else if (selectedScript === SCRIPT.update_parent_agency_kraft_lake) {
        await ParentAgencyExecute();
      }
      else if (selectedScript === SCRIPT.insert_underwriter) {
        await InsertUnderwriterExecute();
      }
      else {
        await ClaimCoverageExecute();
      }
    } catch (error) {
      const msg = getEndPointErrorMessage(error);
      setErrorMessage(msg);
      setShowErrorModal(true);
      return;
    }
    // Simulate script execution
    setTimeout(() => {
      const success = Math.random() > 0.1; // 90% success rate simulation
      if (success) {
        // // Create after data with updated values based on script type
        // if (currentScript?.supportsMultiRow) {
        //   const afterRows = parameterRows.map(row => ({
        //     ...row,
        //     values: { ...row.values, status: MESSAGES.executionStatusUpdated }
        //   }));
        //   setPreviewData(prev => ({
        //     ...prev,
        //     after: afterRows
        //   }));
        // } else {
        //   const afterRow: ParameterRow = {
        //     id: DEFAULT_PARAMETER_ROW_ID,
        //     values: { ...parameterValues, status: MESSAGES.executionStatusUpdated }
        //   };
        //   setPreviewData(prev => ({
        //     ...prev,
        //     after: [afterRow]
        //   }));
        // }
        if (environmentImpact === PRODUCTION_ENVIRONMENT_IMPACT && !isInitiateEmail) {
          setExecuteDisable(true);
        } else {
          setExecuteDisable(false);
        }
        setIsExecuted(true);
        setShowSuccessModal(true);
      } else {
        setErrorMessage(MESSAGES.executionFailed);
        setShowErrorModal(true);
      }
    }, EXECUTION_DELAY_MS);
  };
  return (
    <div className="script-run-container">
      <div className="script-run-header">
        <h1 className="script-run-title">Data Script Run UI</h1>
        <p className="script-run-subtitle">
          Execute parameterized data-fix scripts for operational tasks
        </p>
      </div>
      <div >
        {<Breadcrumb
          items={[
            { label: "Home", path: "/" },
            { label: "auto script", path: "" }
          ]}
        />}
      </div>
      <div className="script-run-content">
        {/* Section 1: Data Fix Script Section - REUSABLE */}
        <DataFixScriptSection
          selectedScript={selectedScript}
          onScriptChange={handleScriptChange}
          scriptOptions={SCRIPT_OPTIONS}
          description={currentScriptOption?.description}
          impact={currentScriptOption?.impact}
        />
        {/* Section 2: Dynamic Parameters Section - REUSABLE & CONFIGURABLE */}
        {currentScript && !currentScript.supportsMultiRow && (
          <DynamicParametersSection
            fields={currentScript.parameters}
            values={parameterValues}
            onChange={handleParameterChange}
            allowMultipleRows={false}
          />
        )}
        {/* Section 2b: Multi-Row Parameters Section */}
        {currentScript && currentScript.supportsMultiRow && (
          <DynamicParametersSection
            fields={currentScript.parameters}
            rows={parameterRows}
            onRowChange={handleRowChange}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            allowMultipleRows={true}
          />
        )}
        {/* Section 3: Incident Details Section - REUSABLE */}
        {currentScript && (
          <IncidentDetailsSection
            incidentId={incidentId}
            requesterEmail={requesterEmail}
            environmentImpact={environmentImpact}
            publishToProd={publishToProd}
            onIncidentChange={handleIncidentChange}
            onIncidentBlur={handleIncidentBlur}
            onEmailChange={(e) => setRequesterEmail(e.target.value)}
            onEnvironmentChange={(e) => setEnvironmentImpact(e.target.value)}
            onPublishChange={handlePublishChange}
          />
        )}
        {/* Preview Section - Reusable Component */}
        {showPreview && currentScript && (
          <PreviewSection
            title="Policy Snapshot Preview"
            fields={previewFields.length > 0 ? previewFields : currentScript.parameters}
            beforeData={previewData.before}
            afterData={previewData.after}
            isExecuted={isExecuted}
            showTimestamp={false}
          />
        )}
        {/* Action Buttons */}
        {currentScript && (
          <div className="action-buttons">
            <Button variant="secondary" onClick={handleReset} size="large" disabled={isExecuteDisabled}>
              Reset
            </Button>
            <Button variant="success" onClick={handleExecute} size="large" disabled={isExecuteActionDisabled}>
              {isInitiateEmail ? 'Initiate Approval' : 'Execute'}
            </Button>
            <Button variant="info" onClick={handlePreview} size="large" disabled={isPreviewDisabled}>
              Preview
            </Button>
          </div>
        )}

      </div>
      {/* Modals */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Confirm Reset"
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">Are you sure, you want to reset?</p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowResetModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmReset}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showExecuteModal}
        onClose={() => setShowExecuteModal(false)}
        title={isInitiateEmail ? 'Confirm Initiate Email' : 'Confirm Execution'}
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">
            {isInitiateEmail
              ? 'Are you sure you want to initiate the email?'
              : 'Are you sure, you want to execute the script?'}
          </p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setShowExecuteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="success" onClick={confirmExecute}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showPublishModal}
        onClose={cancelPublishToProd}
        title="Publish to Production"
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">
            A notification will be sent to the approver before publishing to production
          </p>
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmPublishToProd}>
              OK
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        size="small"
      >
        <div className="modal-content-wrapper success-modal">
          <div className="success-icon">✓</div>
          <p className="modal-message">
            {isInitiateEmail
              ? 'Mail sent successfully for approval!'
              : 'Success: Data Script Executed Successfully!'}
          </p>
          <div className="modal-actions">
            <Button
              variant="success"
              onClick={() => setShowSuccessModal(false)}
              fullWidth
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        size="small"
      >
        <div className="modal-content-wrapper error-modal">
          <div className="error-icon">✕</div>
          <p className="modal-message">
            {errorMessage === MESSAGES.incorrectIncidentId || errorMessage === MESSAGES.invalidSubcompany ? (
              errorMessage
            ) : (
              <>
                Error: Data Script Execution Failed
                <br />
                <span className="error-reason">{errorMessage}</span>
              </>
            )}
          </p>
          <div className="modal-actions">
            <Button
              variant="danger"
              onClick={() => setShowErrorModal(false)}
              fullWidth
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default ScriptRunUI;